import React, { useState, type FC } from 'react'
import { Layout, Model, type TabNode, type IJsonModel } from 'flexlayout-react'
import 'flexlayout-react/style/light.css'
import FundingRequestTab from '../../Ops/fr-pop-up/FundingRequestTab'
import AssetBucketTab from '../../Risk-Officer/AssetBucketTab'
import OverviewTab from '../overview'
import { type PopupScreenProps } from '../../Cap-Trader/utils/types'
import { GET_ASSETS_WITH_FR_ID, GET_FUNDING_REQUEST_BY_ID } from '../../../components/graphql/queries'
import { useQueryWithExtendedTime } from '../../../hooks/useQueryWithExtendedTime'
import { useUserEmail } from '../../../hooks/useUserEmail'
import closeIcon from '../../../utility/img/cancel.png'

interface ExtendedPopupProps extends PopupScreenProps {
  frOnly?: boolean
}

const PopupScreen: FC<ExtendedPopupProps> = ({
  show,
  onClose,
  stage,
  fundingRequestADCId,
  setMessage,
  setMessageColor,
  frOnly = false
}) => {
  const {
    data: assetDetailsData,
    loading: assetLoading,
    error: assetError,
    QueryWithExtendedTimeComponent: AssetQueryWithExtendedTime
  } = useQueryWithExtendedTime({
    query: GET_ASSETS_WITH_FR_ID,
    options: { variables: { fundingRequestId: fundingRequestADCId } }
  })

  const { userEmail } = useUserEmail()

  const {
    data: fundingRequestData,
    loading: fundingLoading,
    error: fundingError,
    QueryWithExtendedTimeComponent: FundingQueryWithExtendedTime,
    refetch
  } = useQueryWithExtendedTime({
    query: GET_FUNDING_REQUEST_BY_ID,
    options: { variables: { fundingRequestId: fundingRequestADCId, note_user: userEmail } }
  })

  const [model] = useState(() => {
    const layout: IJsonModel = {
      global: {},
      borders: [],
      layout: {
        type: 'row',
        weight: 100,
        children: [{
          type: 'tabset',
          weight: 100,
          selected: 0,
          children: frOnly
            ? [{
                type: 'tab',
                name: 'Funding Request',
                component: 'funding',
                enableClose: false
              }]
            : [{
                type: 'tab',
                name: 'Overview',
                component: 'overview'
              },
              {
                type: 'tab',
                name: 'Funding Request',
                component: 'funding'
              },
              {
                type: 'tab',
                name: 'Asset Details',
                component: 'risk'
              }]
        }]
      }
    }

    return Model.fromJson(layout)
  })

  const factory = (node: TabNode) => {
    const component = node.getComponent()

    if (component === 'funding') {
      if (fundingLoading || fundingError) {
        return <FundingQueryWithExtendedTime />
      }
      return <FundingRequestTab
                fundingRequestData={fundingRequestData?.getFundingRequestByID}
                showTest={frOnly}
                fundingRequestADCId={fundingRequestADCId}
                refetch={refetch}
                onClose={onClose}
                setMessage={setMessage}
                setMessageColor={setMessageColor}
            />
    }

    if (!frOnly) {
      if (component === 'risk') {
        if (assetLoading || assetError) {
          return <AssetQueryWithExtendedTime />
        }
        return (
          <AssetBucketTab
            fundingRequestADCId={fundingRequestADCId}
            assetDetailsData={assetDetailsData?.getFundingRequestAssociatedAssetDetailsById?.detailsData}
            assetType={fundingRequestData?.getFundingRequestByID?.fundingRequestDetails?.assetType}
          />
        )
      }

      if (component === 'overview') {
        return (
          <OverviewTab
            fundingRequestId={fundingRequestADCId}
            frSummaryData={assetDetailsData?.getFundingRequestAssociatedAssetDetailsById?.summaryData}
            frTestData={fundingRequestData?.getFundingRequestByID?.fundingRequestDetails?.fundingRequestTest}
            notes={fundingRequestData?.getFundingRequestByID?.noteData}
            onClose={onClose}
            stage={stage}
            setMessage={setMessage}
            setMessageColor={setMessageColor}
            showTest={!frOnly}
          />
        )
      }
    }

    return <div>Invalid tab</div>
  }

  if (!show) {
    return null
  }

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 z-40"></div>
      <div className="fixed inset-0 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-lg p-4 w-[80vw] h-[80vh] overflow-auto relative">
          <button
            className="absolute top-0 right-1 transition duration-150 ease-in-out rounded-full p-2 hover:text-gray-700 z-10"
            onClick={onClose}
          >
            <img src={closeIcon} alt="Close" width={24} height={24} />
          </button>
          <Layout model={model} factory={factory} />
        </div>
      </div>
    </>
  )
}

export default PopupScreen
